import React, { useState, useEffect } from 'react';
import Layout from '../../layout';
import axiosInstance from '../../lib/axiosInstance';
import { PageLoader } from '../../components/ui/tableSkeleton';
import ReactApexChart from 'react-apexcharts';
import {
    FaUsers,
    FaShoppingBag,
    FaRupeeSign,
    FaChartLine,
    FaUserCheck,
    FaUserPlus,
    FaSync,
    FaDownload,
    FaFunnelDollar,
    FaRegClock,
    FaCrown,
    FaAward,
    FaExclamationTriangle,
    FaArrowUp,
    FaArrowDown
} from 'react-icons/fa';

const AnalyticsEngine = () => {
    const [activeTab, setActiveTab] = useState('unified'); // 'unified' | 'retention' | 'onboarding' | 'products'
    const [range, setRange] = useState('30d');
    const [loading, setLoading] = useState(true);
    const [unifiedData, setUnifiedData] = useState(null);
    const [retentionData, setRetentionData] = useState(null);
    const [onboardingData, setOnboardingData] = useState(null);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        fetchAllAnalytics();
    }, [range]);

    const fetchAllAnalytics = async () => {
        setLoading(true);
        try {
            const [unifiedRes, retentionRes, onboardingRes] = await Promise.all([
                axiosInstance.get(`/admin/analytics/unified?range=${range}`),
                axiosInstance.get(`/admin/analytics/retention?range=${range}`),
                axiosInstance.get(`/admin/analytics/onboarding?range=${range}`)
            ]);

            if (unifiedRes.data.success) setUnifiedData(unifiedRes.data.data);
            if (retentionRes.data.success) setRetentionData(retentionRes.data.data);
            if (onboardingRes.data.success) setOnboardingData(onboardingRes.data.data);
        } catch (error) {
            console.error('Error fetching analytics data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleExportCSV = async (type) => {
        try {
            setExporting(true);
            const response = await axiosInstance.get(`/admin/analytics/export?type=${type}&range=${range}`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `analytics_${type}_${range}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Export failed:', error);
            alert('Failed to export CSV report.');
        } finally {
            setExporting(false);
        }
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(val || 0);
    };

    // Revenue Chart Options & Series
    const getRevenueChart = () => {
        const trend = unifiedData?.salesTrend || [];
        const categories = trend.map(t => t.date);
        const revenue = trend.map(t => parseFloat(t.revenue || 0));
        const orders = trend.map(t => parseInt(t.orders || 0, 10));

        const options = {
            chart: { type: 'area', height: 320, toolbar: { show: false } },
            colors: ['#4F46E5', '#10B981'],
            fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.05 } },
            stroke: { curve: 'smooth', width: 2 },
            xaxis: { categories, labels: { style: { colors: '#6B7280' } } },
            yaxis: [
                { title: { text: 'Revenue (₹)' }, labels: { formatter: (val) => `₹${val}` } },
                { opposite: true, title: { text: 'Orders Count' } }
            ],
            tooltip: { y: [{ formatter: (val) => `₹${val}` }, { formatter: (val) => `${val} orders` }] }
        };

        const series = [
            { name: 'Revenue', data: revenue },
            { name: 'Orders', data: orders }
        ];

        return { options, series };
    };

    // Signup Trend Chart
    const getSignupChart = () => {
        const trend = onboardingData?.signupTrend || [];
        const categories = trend.map(t => t.date);
        const signups = trend.map(t => parseInt(t.signups || 0, 10));

        const options = {
            chart: { type: 'bar', height: 280, toolbar: { show: false } },
            colors: ['#06B6D4'],
            plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
            xaxis: { categories, labels: { style: { colors: '#6B7280' } } },
            yaxis: { title: { text: 'New Signups' } }
        };

        const series = [{ name: 'New Registrations', data: signups }];
        return { options, series };
    };

    // Churn Risk Donut Chart
    const getChurnDonutChart = () => {
        const seg = retentionData?.segmentation || {};
        const labels = ['Active (<30d)', 'At Risk (30-90d)', 'Churned (>90d)', 'Never Ordered'];
        const series = [seg.active || 0, seg.atRisk || 0, seg.churned || 0, seg.neverOrdered || 0];

        const options = {
            chart: { type: 'donut', height: 280 },
            labels,
            colors: ['#10B981', '#F59E0B', '#EF4444', '#9CA3AF'],
            legend: { position: 'bottom' },
            dataLabels: { enabled: true }
        };

        return { options, series };
    };

    return (
        <Layout title={'Advanced Analytics Engine'} active={'analytics-m'}>
            <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
                
                {/* TOP HEADER & CONTROLS */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <FaChartLine className="text-indigo-600" /> Advanced Analytics Engine
                        </h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Real-time customer retention matrix, onboarding conversion funnel & performance intelligence.
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        {/* DATE RANGE FILTER */}
                        <div className="inline-flex rounded-xl p-1 bg-gray-100 border border-gray-200">
                            {[
                                { key: '7d', label: '7 Days' },
                                { key: '30d', label: '30 Days' },
                                { key: '90d', label: '90 Days' },
                                { key: 'ytd', label: 'Year to Date' },
                                { key: 'all', label: 'All Time' }
                            ].map(item => (
                                <button
                                    key={item.key}
                                    onClick={() => setRange(item.key)}
                                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                                        range === item.key
                                            ? 'bg-indigo-600 text-white shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>

                        {/* REFRESH BUTTON */}
                        <button
                            onClick={fetchAllAnalytics}
                            className="p-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-600 transition"
                            title="Refresh Data"
                        >
                            <FaSync className={loading ? 'animate-spin text-indigo-600' : ''} />
                        </button>

                        {/* EXPORT REPORT DROPDOWN */}
                        <div className="relative group">
                            <button
                                disabled={exporting}
                                className="flex items-center gap-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-4 py-2 rounded-xl text-xs font-semibold border border-indigo-200 transition"
                            >
                                <FaDownload /> {exporting ? 'Exporting...' : 'Export Reports'}
                            </button>
                            <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-100 rounded-xl shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-all z-20 p-1">
                                <button
                                    onClick={() => handleExportCSV('sales')}
                                    className="w-full text-left px-3 py-2 text-xs font-medium text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 rounded-lg"
                                >
                                    Sales & Orders CSV
                                </button>
                                <button
                                    onClick={() => handleExportCSV('retention')}
                                    className="w-full text-left px-3 py-2 text-xs font-medium text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 rounded-lg"
                                >
                                    Customer Cohorts CSV
                                </button>
                                <button
                                    onClick={() => handleExportCSV('onboarding')}
                                    className="w-full text-left px-3 py-2 text-xs font-medium text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 rounded-lg"
                                >
                                    Onboarding Users CSV
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* TAB NAVIGATION */}
                <div className="flex border-b border-gray-200 space-x-6 overflow-x-auto">
                    {[
                        { id: 'unified', label: 'Unified Overview' },
                        { id: 'retention', label: 'Customer Retention' },
                        { id: 'onboarding', label: 'Customer Onboarding' },
                        { id: 'products', label: 'Products & Brands' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`pb-3 text-sm font-bold border-b-2 whitespace-nowrap transition-colors ${
                                activeTab === tab.id
                                    ? 'border-indigo-600 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* LOADING STATE */}
                {loading && (
                    <PageLoader label="Fetching analytics metrics..." />
                )}

                {!loading && (
                    <>
                        {/* SECTION 1: UNIFIED OVERVIEW */}
                        {activeTab === 'unified' && (
                            <div className="space-y-6">
                                {/* EXECUTIVE KPI CARDS */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Revenue</span>
                                            <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600"><FaRupeeSign className="text-lg" /></div>
                                        </div>
                                        <div className="text-2xl font-black text-gray-900 mt-2">
                                            {formatCurrency(unifiedData?.kpis?.totalRevenue)}
                                        </div>
                                        <div className="text-xs text-emerald-600 font-medium mt-1 flex items-center gap-1">
                                            <FaArrowUp /> Successful completed orders
                                        </div>
                                    </div>

                                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Orders</span>
                                            <div className="p-2.5 rounded-xl bg-blue-50 text-blue-600"><FaShoppingBag className="text-lg" /></div>
                                        </div>
                                        <div className="text-2xl font-black text-gray-900 mt-2">
                                            {unifiedData?.kpis?.totalOrders || 0}
                                        </div>
                                        <div className="text-xs text-gray-500 font-medium mt-1">
                                            AOV: {formatCurrency(unifiedData?.kpis?.avgOrderValue)}
                                        </div>
                                    </div>

                                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">New Customer Signups</span>
                                            <div className="p-2.5 rounded-xl bg-cyan-50 text-cyan-600"><FaUserPlus className="text-lg" /></div>
                                        </div>
                                        <div className="text-2xl font-black text-gray-900 mt-2">
                                            {unifiedData?.kpis?.newUsers || 0}
                                        </div>
                                        <div className="text-xs text-cyan-600 font-medium mt-1">
                                            {unifiedData?.kpis?.purchasingCustomers || 0} unique buyers
                                        </div>
                                    </div>

                                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Repeat Purchase Rate</span>
                                            <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-600"><FaUserCheck className="text-lg" /></div>
                                        </div>
                                        <div className="text-2xl font-black text-gray-900 mt-2">
                                            {retentionData?.summary?.repeatPurchaseRate || 0}%
                                        </div>
                                        <div className="text-xs text-emerald-600 font-medium mt-1">
                                            {retentionData?.summary?.repeatBuyers || 0} multi-order customers
                                        </div>
                                    </div>
                                </div>

                                {/* EXECUTIVE INSIGHTS ALERT BOX */}
                                {unifiedData?.insights?.length > 0 && (
                                    <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-5 rounded-2xl shadow-md">
                                        <div className="flex items-center gap-2 font-bold text-base mb-2">
                                            <FaCrown className="text-yellow-300 text-xl" /> Executive Intelligence Summary
                                        </div>
                                        <ul className="space-y-1 text-sm text-indigo-50">
                                            {unifiedData.insights.map((ins, idx) => (
                                                <li key={idx} className="flex items-center gap-2">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-yellow-300"></span>
                                                    {ins}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* REVENUE & SALES TREND CHART */}
                                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                                    <h3 className="text-base font-bold text-gray-900 mb-4">Revenue & Sales Volume Trend</h3>
                                    {unifiedData?.salesTrend?.length > 0 ? (
                                        <ReactApexChart
                                            options={getRevenueChart().options}
                                            series={getRevenueChart().series}
                                            type="area"
                                            height={320}
                                        />
                                    ) : (
                                        <div className="text-center py-12 text-gray-400 text-sm">No revenue data available for this range.</div>
                                    )}
                                </div>

                                {/* PAYMENT MODES & STATUS BREAKDOWN */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Payment Mode Distribution */}
                                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                                        <h3 className="text-base font-bold text-gray-900 mb-3">Payment Method Breakdown</h3>
                                        <div className="space-y-3">
                                            {(unifiedData?.paymentModes || []).map((pm, i) => (
                                                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                                                    <span className="font-semibold text-gray-800 uppercase text-xs tracking-wider">{pm.paymentMode}</span>
                                                    <div className="text-right">
                                                        <div className="text-sm font-bold text-gray-900">{formatCurrency(pm.amount)}</div>
                                                        <div className="text-xs text-gray-500">{pm.count} orders</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Order Status Breakdown */}
                                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                                        <h3 className="text-base font-bold text-gray-900 mb-3">Order Status Breakdown</h3>
                                        <div className="space-y-3">
                                            {(unifiedData?.orderStatuses || []).map((st, i) => (
                                                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                                                    <span className="font-medium text-gray-700 capitalize text-sm">{st.orderStatus}</span>
                                                    <span className="px-3 py-1 bg-indigo-100 text-indigo-800 text-xs font-bold rounded-full">{st.count} orders</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* SECTION 2: CUSTOMER RETENTION */}
                        {activeTab === 'retention' && (
                            <div className="space-y-6">
                                {/* RETENTION KPI STATS */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                                        <div className="text-xs font-semibold text-gray-500 uppercase">Repeat Purchase Rate</div>
                                        <div className="text-3xl font-black text-emerald-600 mt-2">
                                            {retentionData?.summary?.repeatPurchaseRate || 0}%
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            {retentionData?.summary?.repeatBuyers} out of {retentionData?.summary?.totalBuyers} buyers
                                        </div>
                                    </div>

                                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                                        <div className="text-xs font-semibold text-gray-500 uppercase">Customer Lifetime Value</div>
                                        <div className="text-3xl font-black text-indigo-600 mt-2">
                                            {formatCurrency(retentionData?.summary?.customerLifetimeValue)}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">Avg revenue per customer</div>
                                    </div>

                                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                                        <div className="text-xs font-semibold text-gray-500 uppercase">Avg Repeat Interval</div>
                                        <div className="text-3xl font-black text-blue-600 mt-2">
                                            {retentionData?.summary?.avgRepeatIntervalDays || 0} <span className="text-sm font-normal text-gray-500">Days</span>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">Time between 1st & 2nd order</div>
                                    </div>

                                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                                        <div className="text-xs font-semibold text-gray-500 uppercase">Avg Orders / Customer</div>
                                        <div className="text-3xl font-black text-purple-600 mt-2">
                                            {retentionData?.summary?.avgOrdersPerCustomer || 0}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">Order frequency ratio</div>
                                    </div>
                                </div>

                                {/* CHURN SEGMENTATION & DONUT CHART */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                                        <h3 className="text-base font-bold text-gray-900 mb-2">Customer Activity & Churn Risk</h3>
                                        <p className="text-xs text-gray-500 mb-4">Breakdown of customer activity based on last order recency.</p>
                                        {retentionData?.segmentation && (
                                            <ReactApexChart
                                                options={getChurnDonutChart().options}
                                                series={getChurnDonutChart().series}
                                                type="donut"
                                                height={280}
                                            />
                                        )}
                                    </div>

                                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex flex-col justify-between">
                                        <div>
                                            <h3 className="text-base font-bold text-gray-900 mb-3">Customer Segments Action Summary</h3>
                                            <div className="space-y-3">
                                                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-100 flex items-center justify-between">
                                                    <div>
                                                        <div className="text-xs font-bold text-emerald-800">Active Customers (&lt;30d)</div>
                                                        <div className="text-xs text-emerald-600">High engagement & recurring buyers</div>
                                                    </div>
                                                    <span className="text-lg font-black text-emerald-700">{retentionData?.segmentation?.active || 0}</span>
                                                </div>

                                                <div className="p-3 bg-amber-50 rounded-xl border border-amber-100 flex items-center justify-between">
                                                    <div>
                                                        <div className="text-xs font-bold text-amber-800">At-Risk Customers (30-90d)</div>
                                                        <div className="text-xs text-amber-600">Target with re-engagement coupons</div>
                                                    </div>
                                                    <span className="text-lg font-black text-amber-700">{retentionData?.segmentation?.atRisk || 0}</span>
                                                </div>

                                                <div className="p-3 bg-rose-50 rounded-xl border border-rose-100 flex items-center justify-between">
                                                    <div>
                                                        <div className="text-xs font-bold text-rose-800">Churned Customers (&gt;90d)</div>
                                                        <div className="text-xs text-rose-600">Inactive buyers requiring winback campaigns</div>
                                                    </div>
                                                    <span className="text-lg font-black text-rose-700">{retentionData?.segmentation?.churned || 0}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* COHORT RETENTION MATRIX */}
                                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <h3 className="text-base font-bold text-gray-900">Monthly Retention Cohort Matrix</h3>
                                            <p className="text-xs text-gray-500">% of registered users placing orders in subsequent months.</p>
                                        </div>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border-collapse">
                                            <thead>
                                                <tr className="border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase bg-gray-50">
                                                    <th className="py-3 px-4">Cohort Month</th>
                                                    <th className="py-3 px-4">Users</th>
                                                    <th className="py-3 px-4">Month 0</th>
                                                    <th className="py-3 px-4">Month +1</th>
                                                    <th className="py-3 px-4">Month +2</th>
                                                    <th className="py-3 px-4">Month +3</th>
                                                    <th className="py-3 px-4">Month +6</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100 text-xs font-medium text-gray-700">
                                                {(retentionData?.cohorts || []).map((cohort, idx) => (
                                                    <tr key={idx} className="hover:bg-gray-50">
                                                        <td className="py-3 px-4 font-bold text-gray-900">{cohort.cohortMonth}</td>
                                                        <td className="py-3 px-4 font-semibold text-indigo-600">{cohort.initialCohortSize}</td>
                                                        <td className="py-3 px-4"><span className="px-2 py-1 rounded bg-indigo-100 text-indigo-800 font-bold">{cohort.m0Pct}%</span> ({cohort.m0})</td>
                                                        <td className="py-3 px-4"><span className="px-2 py-1 rounded bg-emerald-100 text-emerald-800 font-bold">{cohort.m1Pct}%</span> ({cohort.m1})</td>
                                                        <td className="py-3 px-4"><span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700 font-semibold">{cohort.m2Pct}%</span> ({cohort.m2})</td>
                                                        <td className="py-3 px-4"><span className="px-2 py-1 rounded bg-blue-50 text-blue-700 font-semibold">{cohort.m3Pct}%</span> ({cohort.m3})</td>
                                                        <td className="py-3 px-4"><span className="px-2 py-1 rounded bg-purple-50 text-purple-700 font-semibold">{cohort.m6Pct}%</span> ({cohort.m6})</td>
                                                    </tr>
                                                ))}
                                                {(!retentionData?.cohorts || retentionData.cohorts.length === 0) && (
                                                    <tr>
                                                        <td colSpan="7" className="py-8 text-center text-gray-400">No cohort matrix data available.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* SECTION 3: CUSTOMER ONBOARDING */}
                        {activeTab === 'onboarding' && (
                            <div className="space-y-6">
                                {/* ONBOARDING FUNNEL BAR */}
                                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                                    <h3 className="text-base font-bold text-gray-900 mb-2">Customer Onboarding Conversion Funnel</h3>
                                    <p className="text-xs text-gray-500 mb-6">User progression from registration to first purchase.</p>

                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-2xl text-center">
                                            <div className="text-xs font-bold text-indigo-600 uppercase">1. Registered</div>
                                            <div className="text-3xl font-black text-indigo-900 mt-2">{onboardingData?.funnel?.registered || 0}</div>
                                            <div className="text-xs text-indigo-500 mt-1">100% of accounts</div>
                                        </div>

                                        <div className="p-4 bg-cyan-50 border border-cyan-100 rounded-2xl text-center">
                                            <div className="text-xs font-bold text-cyan-600 uppercase">2. Verified</div>
                                            <div className="text-3xl font-black text-cyan-900 mt-2">{onboardingData?.funnel?.verified || 0}</div>
                                            <div className="text-xs text-cyan-600 mt-1">{onboardingData?.funnel?.verifiedRate}% verified</div>
                                        </div>

                                        <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl text-center">
                                            <div className="text-xs font-bold text-amber-600 uppercase">3. Added Cart/Wishlist</div>
                                            <div className="text-3xl font-black text-amber-900 mt-2">{onboardingData?.funnel?.interested || 0}</div>
                                            <div className="text-xs text-amber-600 mt-1">{onboardingData?.funnel?.interestedRate}% high intent</div>
                                        </div>

                                        <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-2xl text-center">
                                            <div className="text-xs font-bold text-emerald-600 uppercase">4. Placed First Order</div>
                                            <div className="text-3xl font-black text-emerald-900 mt-2">{onboardingData?.funnel?.converted || 0}</div>
                                            <div className="text-xs text-emerald-600 mt-1 font-bold">{onboardingData?.funnel?.conversionRate}% final conversion</div>
                                        </div>
                                    </div>
                                </div>

                                {/* SIGNUP VELOCITY & TIME TO FIRST ORDER */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                                        <h3 className="text-base font-bold text-gray-900 mb-3">Registration Velocity</h3>
                                        {onboardingData?.signupTrend?.length > 0 ? (
                                            <ReactApexChart
                                                options={getSignupChart().options}
                                                series={getSignupChart().series}
                                                type="bar"
                                                height={280}
                                            />
                                        ) : (
                                            <div className="py-12 text-center text-gray-400 text-sm">No signup trend data.</div>
                                        )}
                                    </div>

                                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm space-y-4">
                                        <h3 className="text-base font-bold text-gray-900">First-Order Conversion Windows</h3>

                                        <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                            <div className="text-xs font-semibold text-gray-500">Avg Time to First Order</div>
                                            <div className="text-2xl font-black text-indigo-600 mt-1">
                                                {onboardingData?.avgDaysToFirstOrder || 0} <span className="text-sm text-gray-500 font-medium">Days</span>
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between text-xs font-semibold text-gray-700">
                                                <span>Ordered Within 24 Hours</span>
                                                <span className="text-indigo-600 font-bold">{onboardingData?.conversionWindows?.within24hRate}% ({onboardingData?.conversionWindows?.within24h})</span>
                                            </div>
                                            <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                                                <div className="bg-indigo-600 h-2 rounded-full" style={{ width: `${onboardingData?.conversionWindows?.within24hRate || 0}%` }}></div>
                                            </div>

                                            <div className="flex items-center justify-between text-xs font-semibold text-gray-700">
                                                <span>Ordered Within 7 Days</span>
                                                <span className="text-indigo-600 font-bold">{onboardingData?.conversionWindows?.within7dRate}% ({onboardingData?.conversionWindows?.within7d})</span>
                                            </div>
                                            <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                                                <div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${onboardingData?.conversionWindows?.within7dRate || 0}%` }}></div>
                                            </div>

                                            <div className="flex items-center justify-between text-xs font-semibold text-gray-700">
                                                <span>Ordered Within 30 Days</span>
                                                <span className="text-indigo-600 font-bold">{onboardingData?.conversionWindows?.within30dRate}% ({onboardingData?.conversionWindows?.within30d})</span>
                                            </div>
                                            <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                                                <div className="bg-indigo-400 h-2 rounded-full" style={{ width: `${onboardingData?.conversionWindows?.within30dRate || 0}%` }}></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* SECTION 4: PRODUCTS & BRANDS */}
                        {activeTab === 'products' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* TOP SELLING PRODUCTS */}
                                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                                    <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                                        <FaAward className="text-amber-500" /> Top 5 Bestselling Products
                                    </h3>
                                    <div className="space-y-3">
                                        {(unifiedData?.topProducts || []).map((prod, i) => (
                                            <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                                                <div className="flex items-center gap-3">
                                                    <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-800 text-xs font-bold flex items-center justify-center">
                                                        {i + 1}
                                                    </span>
                                                    <div>
                                                        <div className="text-xs font-bold text-gray-900 line-clamp-1">{prod.productName}</div>
                                                        <div className="text-xs text-gray-500">{prod.totalQuantity} units sold across {prod.ordersCount} orders</div>
                                                    </div>
                                                </div>
                                                <div className="text-xs font-black text-indigo-600">
                                                    {formatCurrency(prod.totalRevenue)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* TOP BRANDS LEADERBOARD */}
                                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                                    <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                                        <FaCrown className="text-yellow-500" /> Top Performing Brands
                                    </h3>
                                    <div className="space-y-3">
                                        {(unifiedData?.topBrands || []).map((brand, i) => (
                                            <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                                                <div className="flex items-center gap-3">
                                                    <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-800 text-xs font-bold flex items-center justify-center">
                                                        {i + 1}
                                                    </span>
                                                    <div>
                                                        <div className="text-xs font-bold text-gray-900">{brand.brandName || 'Direct Brand'}</div>
                                                        <div className="text-xs text-gray-500">{brand.ordersCount} completed orders</div>
                                                    </div>
                                                </div>
                                                <div className="text-xs font-black text-emerald-600">
                                                    {formatCurrency(brand.brandRevenue)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
};

export default AnalyticsEngine;
